"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { Field } from "@base-ui/react/field";
import { Select as BaseSelect } from "@base-ui/react/select";
import { cn } from "@marketing-sites/utils/cn";
import type { ValidationRule } from "@marketing-sites/utils/validation";
import { composeValidate } from "@marketing-sites/utils/validation";
import {
  FieldErrors,
  type FieldErrorsProps,
} from "../form/validation/field-errors";

export type SelectOption = {
  label: string;
  value: string;
};

export type SelectProps = BaseSelect.Root.Props<string, false> & {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  className?: string;
  icon?: ReactNode;
  itemIndicator?: ReactNode;
  /** Props forwarded to Select Elements. */
  fieldProps?: Omit<Field.Root.Props, "name" | "validate">;
  validations?: ValidationRule[];
  errorProps?: FieldErrorsProps["errorProps"];
  requiredMessage?: string;
  triggerProps?: BaseSelect.Trigger.Props;
  positionerProps?: BaseSelect.Positioner.Props;
  popupProps?: BaseSelect.Popup.Props;
  itemProps?: Omit<BaseSelect.Item.Props, "value">;
};

/**
 * A labelled select dropdown with built-in error display.
 *
 * Must be rendered inside a Base UI `<Form>` for validation and error
 * messages to work. Without it, `Field.Error` will never appear because
 * the validation lifecycle (`field.validate()` on submit) is not triggered.
 *
 * @see https://base-ui.com/react/components/select
 * @see https://base-ui.com/react/components/field
 */
export function Select({
  name,
  label,
  placeholder,
  options,
  required,
  disabled,
  defaultValue,
  value,
  onValueChange,
  className,
  icon = <ChevronIcon />,
  itemIndicator = <CheckIcon />,
  fieldProps,
  validations,
  errorProps,
  requiredMessage,
  triggerProps,
  positionerProps,
  popupProps,
  itemProps,
  ...rootProps
}: SelectProps) {
  // Base UI Select updates a hidden <input> via React state but doesn't
  // dispatch a native change event. Fire one so form-level onChange handlers
  // (e.g. MultiStepForm's isLastStep recomputation) are notified.
  const fieldRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    fieldRef.current?.dispatchEvent(new Event("change", { bubbles: true }));
  }, [value]);

  return (
    <Field.Root
      name={name}
      disabled={disabled}
      validate={composeValidate(validations, required, requiredMessage)}
      {...fieldProps}
      ref={fieldRef}
    >
      {label && (
        <Field.Label className="label">
          {label} {required && <span>*</span>}
        </Field.Label>
      )}
      <BaseSelect.Root
        name={name}
        required={required}
        disabled={disabled}
        defaultValue={defaultValue}
        value={value}
        onValueChange={onValueChange}
        items={options}
        {...rootProps}
      >
        <BaseSelect.Trigger
          aria-required={required || undefined}
          {...triggerProps}
          className={cn(
            "select flex w-full items-center justify-between",
            className,
            triggerProps?.className,
          )}
        >
          <BaseSelect.Value placeholder={placeholder} />
          <BaseSelect.Icon className="flex items-center">
            {icon}
          </BaseSelect.Icon>
        </BaseSelect.Trigger>
        <BaseSelect.Positioner
          side="bottom"
          align="start"
          alignItemWithTrigger={false}
          sideOffset={0}
          positionMethod="fixed"
          {...positionerProps}
          className={cn("w-(--anchor-width) z-50", positionerProps?.className)}
        >
          <BaseSelect.Popup
            {...popupProps}
            className={cn(
              "select-popup overflow-y-auto border shadow-md",
              popupProps?.className,
            )}
          >
            {options.map((option) => (
              <BaseSelect.Item
                key={option.value}
                value={option.value}
                {...itemProps}
                className={cn(
                  "select-item flex cursor-default items-center gap-2",
                  itemProps?.className,
                )}
              >
                <BaseSelect.ItemIndicator className="flex w-4 items-center justify-center">
                  {itemIndicator}
                </BaseSelect.ItemIndicator>
                <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
              </BaseSelect.Item>
            ))}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Root>
      <FieldErrors errorProps={errorProps} requiredMessage={requiredMessage} />
    </Field.Root>
  );
}

function ChevronIcon() {
  return (
    <svg width="8" height="5" fill="none" viewBox="0 0 8 5">
      <path
        fill="currentColor"
        d="M7.21 0a.25.25 0 0 1 .195.406l-3.48 4.35a.25.25 0 0 1-.39 0L.055.406A.25.25 0 0 1 .25 0z"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 6l3 3 5-5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
